<template>
  <v-skeleton-loader
    v-if="isLoading"
    type="table"
  />

  <div v-else>
    <v-dialog
      :value="isCreating"
      width="500"
      persistent
    >
      <v-card>
        <v-card-title>
          諸費ラベル
        </v-card-title>

        <v-card-text>
          <v-radio-group
            v-model="addTarget.type"
            row
          >
            <v-radio
              v-for="(selectTag, selectTagIdx) in selectTags"
              :key="`selectTags[${selectTagIdx}]`"
              :label="selectTag.text"
              :value="selectTag.value"
            ></v-radio>
          </v-radio-group>

          <v-text-field
            v-model="addTarget.name"
            :error="!addTarget.name"
            label="名前"
            required
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            :ripple="false"
            @click="create"
          >
            作成
          </v-btn>
          <v-btn
            color="blue darken-1"
            text
            :ripple="false"
            @click="$emit('close')"
          >
            キャンセル
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-data-table
      ref="dataTable"
      :headers="headers"
      :items="expenseTags"
      group-by="displayType"
      item-key="kid"
      :header-props="{ sortByText: 'ソート' }"
      hide-default-footer
      disable-pagination
      no-data-text="データがありません"
    >
      <template #[`item.attributes.name`]="{item}">
        <yona-edit-dialog
          :display-name="item.name"
          @open="edit(item)"
          @save="update"
        >
          <template #input>
            <v-text-field
              v-if="isEditing"
              v-model="editTarget.name"
              placeholder="名前"
            />
          </template>
        </yona-edit-dialog>
      </template>

      <template #[`group.header`]="{group}">
        <td :colspan="headers.length">
          {{ group }}
        </td>
      </template>

      <template #[`item.actions`]="{item}">
        <yona-edit-dialog
          title="削除の確認"
          btn-color="error"
          save-text="同意の上で削除"
          @save="destroy(item)"
        >
          <template #append-outer-display-name>
            <v-btn
              icon
              :ripple="false"
            >
              <v-icon small>
                {{ icons.mdiTrashCanOutline }}
              </v-icon>
            </v-btn>
          </template>

          <template #input>
            <span>
              このラベルに関連しているすべての諸費データも削除します。削除を実行してもよろしいですか
            </span>
          </template>
        </yona-edit-dialog>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import {
  ref,
  reactive,
  getCurrentInstance,
} from '@vue/composition-api'
import {
  mdiTrashCanOutline,
  mdiPlus,
} from '@mdi/js'
import ExpenseTagApi from '@/api/waiter/ExpenseTag'
import YonaEditDialog from '@/views/components/util/YonaEditDialog.vue'

export default {
  components: {
    YonaEditDialog,
  },
  props: {
    isCreating: {
      type: Boolean,
      default: false,
    },
  },
  setup(_, { emit }) {
    const vm = getCurrentInstance().proxy

    const selectTags = [
      { text: '大ラベル', value: 'expense_primary_label' },
      { text: '小ラベル', value: 'expense_secondary_label' },
    ]

    const isLoading = ref(false)
    const isEditing = ref(false)
    const expenseTags = ref([])

    const editTarget = reactive({ id: null, name: null, type: '' })
    const addTarget = reactive({ name: '', type: selectTags[0].value })

    const setType = type => tag => {
      return {
        ...tag,
        displayType: type === 'expense_primary_label' ? '大ラベル' : '小ラベル',
        kid: `${type}_${tag.id}`,
        type,
      }
    }

    const updateRefTags = res => {
      const { primaryTags, secondaryTags } = res.data.expenseTags.data.attributes.tags

      expenseTags.value = [
        ...primaryTags.map(setType('expense_primary_label')),
        ...secondaryTags.map(setType('expense_secondary_label')),
      ]
    }

    const edit = item => {
      isEditing.value = true

      editTarget.id = item.id
      editTarget.name = item.name
      editTarget.type = item.type
    }

    const create = async () => {
      if (!addTarget.name || !addTarget.type) return
      const res = await ExpenseTagApi.createExpenseTag(addTarget)

      if (res?.data) {
        updateRefTags(res)
        vm.$toast.success('諸費ラベルを作成しました')
      }

      addTarget.name = ''
      addTarget.type = selectTags[0].value
      emit('close')
    }

    const update = async index => {
      const { id, name, type } = editTarget
      const res = await ExpenseTagApi.updateExpenseTag({ id, name, type })

      if (res?.data) {
        updateRefTags(res)
        vm.$toast.success('諸費ラベルを更新しました')
      }

      isEditing.value = false
      editTarget.id = null
      editTarget.name = ''
      editTarget.type = ''
    }

    const destroy = async ({ id, type }) => {
      const res = await ExpenseTagApi.deleteExpenseTag({ id, type })

      if (res?.data) {
        updateRefTags(res)
        vm.$toast.success('諸費ラベルを削除しました')
      }
    }

    const getExpenseTags = async () => {
      isLoading.value = true

      const res = await ExpenseTagApi.getExpenseTags()

      if (res?.data) {
        updateRefTags(res)
      }

      isLoading.value = false
    }

    getExpenseTags()

    return {
      // data
      isLoading,
      isEditing,
      expenseTags,
      headers: [
        { text: '名前', value: 'attributes.name' },
        {
          text: '',
          value: 'actions',
          align: 'end',
          sortable: false,
        },
      ],
      selectTags,
      editTarget,
      addTarget,

      // methods
      edit,
      update,
      destroy,
      create,

      icons: {
        mdiTrashCanOutline,
        mdiPlus,
      },
    }
  },
}
</script>
